import { Divider, InputAdornment, Stack } from '@mui/material';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useShowGraphqlUserVisibleErrorSnackbar } from '@ocx-app/hooks/use-show-graphql-user-visible-error-snackbar.hook';
import { buttonTextMessages } from '@ocx-app/modules/localization/button-text.messages';

import { showErrorOnBlurOrDirty, TextField } from '@ocx/ui-forms';
import { Button, SizingMultiplier, Typography } from '@ocx/ui';
import { PayPalIcon, VenmoIcon } from '@ocx/ui-icons';

import { allureCashbackMessages } from './allure-cashback.messages';
import { useLinkAllureCashbackAccount } from './use-link-allure-cashback-account.hook';
import {
  AllureCashbackCashoutTypename,
  AllureCashbackSetupFormValues,
  isPaypalFieldDisabled,
  isSetupFormSubmittable,
  isVenmoFieldDisabled,
  validateCashoutIdentifier,
} from './allure-cashback.utils';

const INITIAL_VALUES: AllureCashbackSetupFormValues = {
  paypalEmail: '',
  venmoEmail: '',
};

export type AllureCashbackSetupPageProps = {
  initialValues?: Partial<AllureCashbackSetupFormValues>;
  onLinked?: () => void;
};

export const AllureCashbackSetupPage = ({ initialValues, onLinked }: AllureCashbackSetupPageProps) => {
  const intl = useIntl();
  const showGraphqlUserVisibleErrorSnackbar = useShowGraphqlUserVisibleErrorSnackbar();
  const { linkCashbackAccount } = useLinkAllureCashbackAccount({
    onCompleted: onLinked,
    onError: showGraphqlUserVisibleErrorSnackbar,
  });
  const [focusedCashout, setFocusedCashout] = useState<AllureCashbackCashoutTypename | null>(null);

  return (
    <Stack spacing={SizingMultiplier.lg} p={SizingMultiplier.lg} useFlexGap>
      <Typography mt={SizingMultiplier.lg} variant="h4" align="center" sx={{ textTransform: 'uppercase' }}>
        <FormattedMessage {...allureCashbackMessages.setupPageTitle} />
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center">
        <FormattedMessage {...allureCashbackMessages.setupPageSubtitle} />
      </Typography>

      <Form<AllureCashbackSetupFormValues>
        initialValues={{ ...INITIAL_VALUES, ...initialValues }}
        onSubmit={linkCashbackAccount}
        render={(form) => {
          const isPaypalDisabled = isPaypalFieldDisabled(form.values, focusedCashout);
          const isVenmoDisabled = isVenmoFieldDisabled(form.values, focusedCashout);
          const canSubmit = isSetupFormSubmittable(form.values);

          return (
            <Stack component="form" onSubmit={form.handleSubmit} spacing={SizingMultiplier.lg} useFlexGap>
              <TextField
                variant="outlined"
                type="text"
                autoComplete="email"
                name="paypalEmail"
                fullWidth
                label={intl.formatMessage(allureCashbackMessages.setupPaypalFieldLabel)}
                disabled={form.submitting || isPaypalDisabled}
                showError={showErrorOnBlurOrDirty}
                fieldProps={{ validate: validateCashoutIdentifier }}
                onFocus={() => setFocusedCashout('CashoutAccountPaypal')}
                onBlur={() => setFocusedCashout((current) => (current === 'CashoutAccountPaypal' ? null : current))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <PayPalIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" alignItems="center" alignSelf="center" spacing={SizingMultiplier.sm} width="35%">
                <Divider sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage {...allureCashbackMessages.setupOrDivider} />
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>

              <TextField
                variant="outlined"
                type="text"
                autoComplete="email"
                name="venmoEmail"
                fullWidth
                label={intl.formatMessage(allureCashbackMessages.setupVenmoFieldLabel)}
                disabled={form.submitting || isVenmoDisabled}
                showError={showErrorOnBlurOrDirty}
                fieldProps={{ validate: validateCashoutIdentifier }}
                onFocus={() => setFocusedCashout('CashoutAccountVenmo')}
                onBlur={() => setFocusedCashout((current) => (current === 'CashoutAccountVenmo' ? null : current))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <VenmoIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={!canSubmit || form.submitting}
                sx={{ textTransform: 'uppercase' }}>
                <FormattedMessage {...buttonTextMessages['button-text:save']} />
              </Button>
            </Stack>
          );
        }}
      />
    </Stack>
  );
};
